import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

export type RecordingStatus = 'uploading' | 'pending' | 'processing' | 'completed' | 'failed';
export type TranscriptionJobStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type TranscriptionMode = 'multichannel' | 'mixed';

export interface RecordingSegment {
  id: number;
  recordingId: number;
  jobId: number;
  // 0 = mix (filtered out by cron, kept in type for safety), 1 = mic, 2 = desktop.
  channel: 0 | 1 | 2;
  startMs: number;
  endMs: number;
  text: string;
  originalText?: string | null;
  language?: string | null;
  confidence?: number | null;
  editedAt?: string | null;
}

export interface TranscriptionJob {
  id: number;
  recordingId: number;
  runIndex: number;
  transcriptionMode: TranscriptionMode;
  status: TranscriptionJobStatus;
  attempts: number;
  lastError?: string | null;
  deepgramRequestId?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Recording {
  id: number;
  consultationId: number;
  studentId?: number | null;
  audioObjectKey: string;
  audioMimeType: string;
  audioSizeBytes?: number | null;
  durationSeconds?: number | null;
  // Which speaker slot is the teacher: 1 (mic / first-detected) or 2 (desktop / second-detected).
  channelTeacher: 1 | 2;
  uploadedBy: number;
  status: RecordingStatus;
  // Which decoding strategy the worker should use on the next run.
  transcriptionMode: TranscriptionMode;
  // false = transcript corrupted by desktop-audio leak (client speech on both
  // tracks). Set by the AI check after transcription; admin-overridable.
  healthy: boolean;
  healthReason?: string | null;
  // Null = the AI check never ran for the active transcript.
  healthCheckedAt?: string | null;
  consultation?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    consultationDate?: string;
  };
  student?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  job?: TranscriptionJob | null;
  jobs?: TranscriptionJob[];
  activeJobId?: number | null;
  viewJobId?: number | null;
  segments?: RecordingSegment[];
  createdAt?: string;
  updatedAt?: string;
}

export interface RecordingsFilters {
  status: string;
  unattributed: boolean;
  consultationId?: number | null;
  studentId?: number | null;
  dateFrom: string;
  dateTo: string;
  search: string;
}

interface RecordingsState {
  recordings: Recording[];
  current: Recording | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  filters: RecordingsFilters;
}

const initialState: RecordingsState = {
  recordings: [],
  current: null,
  loading: false,
  error: null,
  pagination: { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 50 },
  filters: {
    status: 'all',
    unattributed: false,
    consultationId: null,
    studentId: null,
    dateFrom: '',
    dateTo: '',
    search: ''
  }
};

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchRecordingsAsync = createAsyncThunk(
  'recordings/fetch',
  async (params: Partial<RecordingsFilters> & { page?: number; limit?: number } = {}, { rejectWithValue, getState }) => {
    try {
      const qs = new URLSearchParams();
      qs.append('page', String(params.page ?? 1));
      qs.append('limit', String(params.limit ?? 50));
      if (params.status && params.status !== 'all') qs.append('status', params.status);
      if (params.unattributed) qs.append('unattributed', 'true');
      if (params.consultationId) qs.append('consultationId', String(params.consultationId));
      if (params.studentId) qs.append('studentId', String(params.studentId));
      if (params.dateFrom) qs.append('from', params.dateFrom);
      if (params.dateTo) qs.append('to', params.dateTo);
      if (params.search) qs.append('search', params.search);

      const response = await axios.get(`${API_BASE_URL}/recordings?${qs.toString()}`,
        { headers: getAuthHeaders(getState) });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch recordings'));
    }
  }
);

export const fetchRecordingAsync = createAsyncThunk(
  'recordings/fetchOne',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/recordings/${id}`,
        { headers: getAuthHeaders(getState) });
      return response.data.recording as Recording;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch recording'));
    }
  }
);

export const initRecordingUploadAsync = createAsyncThunk(
  'recordings/initUpload',
  async (
    body: { consultationId: number; studentId?: number | null; mimeType: string; sizeBytes: number; durationSeconds?: number | null; transcriptionMode?: TranscriptionMode },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/recordings`, body,
        { headers: getAuthHeaders(getState) });
      return response.data as { recordingId: number; uploadUrl: string; objectKey: string };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to initialize upload'));
    }
  }
);

export const completeRecordingUploadAsync = createAsyncThunk(
  'recordings/completeUpload',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/recordings/${id}/complete`, {},
        { headers: getAuthHeaders(getState) });
      return response.data.recording as Recording;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to finalize upload'));
    }
  }
);

export const updateRecordingAsync = createAsyncThunk(
  'recordings/update',
  async (
    { id, patch }: { id: number; patch: Partial<Pick<Recording, 'studentId' | 'channelTeacher' | 'transcriptionMode' | 'healthy'>> },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/recordings/${id}`, patch,
        { headers: getAuthHeaders(getState) });
      return response.data.recording as Recording;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update recording'));
    }
  }
);

export const addSegmentAsync = createAsyncThunk(
  'recordings/addSegment',
  async (
    { recordingId, channel, startMs, endMs, text }:
      { recordingId: number; channel: 1 | 2; startMs: number; endMs?: number; text: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/recordings/${recordingId}/segments`,
        { channel, startMs, endMs, text },
        { headers: getAuthHeaders(getState) }
      );
      return response.data.segment as RecordingSegment;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to add segment'));
    }
  }
);

export const deleteSegmentAsync = createAsyncThunk(
  'recordings/deleteSegment',
  async (
    { recordingId, segmentId }: { recordingId: number; segmentId: number },
    { rejectWithValue, getState }
  ) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/recordings/${recordingId}/segments/${segmentId}`,
        { headers: getAuthHeaders(getState) }
      );
      return segmentId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete segment'));
    }
  }
);

export const updateSegmentAsync = createAsyncThunk(
  'recordings/updateSegment',
  async (
    { recordingId, segmentId, text }: { recordingId: number; segmentId: number; text: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/recordings/${recordingId}/segments/${segmentId}`,
        { text },
        { headers: getAuthHeaders(getState) }
      );
      return response.data.segment as RecordingSegment;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update segment'));
    }
  }
);

export const getRecordingAudioUrlAsync = createAsyncThunk(
  'recordings/audioUrl',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/recordings/${id}/audio-url`, {},
        { headers: getAuthHeaders(getState) });
      return response.data as { url: string; expiresIn: number };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to get audio URL'));
    }
  }
);

export const reTranscribeAsync = createAsyncThunk(
  'recordings/reTranscribe',
  async (
    { id, transcriptionMode }: { id: number; transcriptionMode?: TranscriptionMode },
    { rejectWithValue, getState }
  ) => {
    try {
      const body: { transcriptionMode?: TranscriptionMode } = {};
      if (transcriptionMode) body.transcriptionMode = transcriptionMode;
      const response = await axios.post(`${API_BASE_URL}/recordings/${id}/re-transcribe`, body,
        { headers: getAuthHeaders(getState) });
      return response.data.recording as Recording;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to re-transcribe recording'));
    }
  }
);

export const deleteRecordingAsync = createAsyncThunk(
  'recordings/delete',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/recordings/${id}`, { headers: getAuthHeaders(getState) });
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete recording'));
    }
  }
);

const recordingsSlice = createSlice({
  name: 'recordings',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    setFilters: (state, action: PayloadAction<Partial<RecordingsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    clearCurrent: (state) => { state.current = null; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecordingsAsync.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchRecordingsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.recordings = action.payload.recordings;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchRecordingsAsync.rejected, (state, action) => {
        state.loading = false; state.error = action.payload as string;
      })
      .addCase(fetchRecordingAsync.fulfilled, (state, action) => { state.current = action.payload; })
      .addCase(completeRecordingUploadAsync.fulfilled, (state, action) => { state.current = action.payload; })
      .addCase(updateRecordingAsync.fulfilled, (state, action) => {
        if (state.current && state.current.id === action.payload.id) {
          state.current = { ...state.current, ...action.payload };
        }
        const idx = state.recordings.findIndex(r => r.id === action.payload.id);
        if (idx !== -1) state.recordings[idx] = { ...state.recordings[idx], ...action.payload };
      })
      .addCase(reTranscribeAsync.fulfilled, (state, action) => {
        if (state.current && state.current.id === action.payload.id) {
          // Keep the previous run's segments visible while the new run processes —
          // activeJobId still points at the prior success until the worker completes.
          state.current = {
            ...state.current,
            ...action.payload,
            segments: state.current.segments
          };
        }
      })
      .addCase(updateSegmentAsync.fulfilled, (state, action) => {
        if (state.current && state.current.segments) {
          const idx = state.current.segments.findIndex(s => s.id === action.payload.id);
          if (idx !== -1) state.current.segments[idx] = action.payload;
        }
      })
      .addCase(addSegmentAsync.fulfilled, (state, action) => {
        if (state.current) {
          state.current.segments = [...(state.current.segments || []), action.payload];
        }
      })
      .addCase(deleteSegmentAsync.fulfilled, (state, action) => {
        if (state.current && state.current.segments) {
          state.current.segments = state.current.segments.filter(s => s.id !== action.payload);
        }
      })
      .addCase(deleteRecordingAsync.fulfilled, (state, action) => {
        state.recordings = state.recordings.filter(r => r.id !== action.payload);
        if (state.current && state.current.id === action.payload) state.current = null;
      });
  }
});

export const { clearError, setFilters, clearFilters, setPage, clearCurrent } = recordingsSlice.actions;
export default recordingsSlice.reducer;
